import { useTick, useMeta, useSubscribe } from "../lib/socket";
import { fmt, fmtInt, compact, istTime } from "../lib/format";
import { isDerivative } from "../lib/format";

function Row({ b, s, maxQ, i }) {
  return (
    <div className="grid grid-cols-6 text-[11px] num py-[3px] relative" data-testid={`depth-row-${i}`}>
      <div className="col-span-3 grid grid-cols-3 relative pr-2">
        <div className="absolute inset-y-0 right-2 bg-up/10 m-breathe" style={{ width: `${b ? (b[1] / maxQ) * 100 : 0}%` }} />
        <span className="relative text-up">{b ? fmt(b[0]) : "—"}</span>
        <span className="relative text-center text-muted-foreground">{b?.[2] || "—"}</span>
        <span className="relative text-right">{b ? fmtInt(b[1]) : "—"}</span>
      </div>
      <div className="col-span-3 grid grid-cols-3 relative pl-2">
        <div className="absolute inset-y-0 left-2 bg-down/10 m-breathe" style={{ width: `${s ? (s[1] / maxQ) * 100 : 0}%` }} />
        <span className="relative text-down">{s ? fmt(s[0]) : "—"}</span>
        <span className="relative text-center text-muted-foreground">{s?.[2] || "—"}</span>
        <span className="relative text-right">{s ? fmtInt(s[1]) : "—"}</span>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="flex justify-between text-[11px] py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="num" data-testid={testid}>{value}</span>
    </div>
  );
}

export function MarketDepth({ instrumentKey, compactMode = false }) {
  useSubscribe([instrumentKey]);
  const t = useTick(instrumentKey) || {};
  const inst = useMeta(instrumentKey);
  const buy = t.depth?.buy || [];
  const sell = t.depth?.sell || [];
  const maxQ = Math.max(1, ...buy.map((x) => x[1]), ...sell.map((x) => x[1]));
  const tb = t.tbq || 0;
  const ts = t.tsq || 0;
  const buyPct = tb + ts ? (tb / (tb + ts)) * 100 : 50;
  return (
    <div data-testid="market-depth-panel" className="space-y-2">
      <div>
        <div className="grid grid-cols-6 eyebrow !tracking-wider pb-1 border-b border-border">
          <span>Bid</span><span className="text-center">Ord.</span><span className="text-right pr-2">Qty</span>
          <span className="pl-2">Offer</span><span className="text-center">Ord.</span><span className="text-right">Qty</span>
        </div>
        {[0, 1, 2, 3, 4].map((i) => <Row key={i} i={i} b={buy[i]} s={sell[i]} maxQ={maxQ} />)}
        <div className="grid grid-cols-2 text-[11px] num pt-1 border-t border-border">
          <span className="text-up" data-testid="depth-total-bid">Total {fmtInt(tb)}</span>
          <span className="text-down text-right" data-testid="depth-total-ask">Total {fmtInt(ts)}</span>
        </div>
        <div className="h-1 mt-1 rounded-full overflow-hidden bg-down/60 flex">
          <div className="bg-up h-full transition-[width] duration-500" style={{ width: `${buyPct}%` }} />
        </div>
      </div>
      {!compactMode && (
        <div className="grid grid-cols-2 gap-x-6">
          <Stat label="Open" value={fmt(t.o)} testid="depth-open" />
          <Stat label="High" value={fmt(t.h)} testid="depth-high" />
          <Stat label="Low" value={fmt(t.l)} testid="depth-low" />
          <Stat label="Prev. close" value={fmt(t.cp)} testid="depth-close" />
          <Stat label="Volume" value={compact(t.v)} testid="depth-volume" />
          <Stat label="Avg. price" value={fmt(t.atp)} testid="depth-atp" />
          {isDerivative(inst) && <Stat label="OI" value={compact(t.oi)} testid="depth-oi" />}
          <Stat label="Lower circuit" value={fmt(t.lc)} testid="depth-lc" />
          <Stat label="Upper circuit" value={fmt(t.uc)} testid="depth-uc" />
          <Stat label="52W high" value={fmt(t.h52)} testid="depth-52h" />
          <Stat label="52W low" value={fmt(t.l52)} testid="depth-52l" />
          <Stat label="LTT" value={t.ltt ? istTime(new Date(Number(t.ltt)).toISOString()) : "—"} testid="depth-ltt" />
        </div>
      )}
    </div>
  );
}
